import { ILocationService } from 'angular';
import { ngStorage } from 'ngstorage';
import { IFontStyleModel, getFontName } from '../../tn.common';

export function consecutiveGroups<T>(
    items: T[],
    compare: (a: T, b: T) => boolean
): T[][] {
    const groups: T[][] = [];
    let currentGroup: T[] = [];
    for (const item of items) {
        if (currentGroup.length === 0 || compare(currentGroup[0], item)) {
            currentGroup.push(item);
        } else {
            groups.push(currentGroup);
            currentGroup = [item];
        }
    }
    if (currentGroup.length > 0) {
        groups.push(currentGroup);
    }
    return groups;
}

export function consecutiveNumberGroups(points: number[]): number[][] {
    return consecutiveGroups<number>(points, (a, b) => a + 1 === b);
}

export function stringToUnicodePoints(str: string): number[] {
    const points: number[] = [];
    for (let i = 0; i < str.length; i++) {
        points.push(str.charCodeAt(i));
    }
    return points;
}

export function pointsToUnicodeRange(points: number[]): string {
    const groups = consecutiveNumberGroups(points);
    return groups
        .map((group) => {
            if (group.length === 1) {
                return `U+${group[0].toString(16)}`;
            } else {
                return `U+${group[0].toString(16)}-${group[
                    group.length - 1
                ].toString(16)}`;
            }
        })
        .join(',');
}

export function stringToUnicodeRange(str: string): string {
    const points = stringToUnicodePoints(str);
    points.sort((a, b) => a - b);
    return pointsToUnicodeRange(points);
}

export class FontCssService {
    static $inject = ['fontServerAddress', '$localStorage', '$location'];

    styleElement: HTMLStyleElement = document.createElement('style');
    styleSheet: CSSStyleSheet = this.styleElement.sheet as CSSStyleSheet;
    allSubsets = !!this.$localStorage.specimenText;
    baseSubset = 'pangram';
    loadedSubsets = {};
    cache: { [font_file_id: number]: { [subset: string]: FontFace } } = {};

    constructor(
        protected fontServerAddress: string,
        protected $localStorage: ngStorage.StorageService,
        protected $location: ILocationService
    ) {
        document.head.appendChild(this.styleElement);
    }

    addFontStyles(font: IFontStyleModel, allSubsets = false): FontFace {
        const specimenTextOnly = !(
            this.allSubsets ||
            this.$location.search().layout === 'overview' ||
            allSubsets
        );
        const target = getFontName(font);
        const font_file_id = font.font_file_id;
        if (!this.cache[font_file_id]) this.cache[font_file_id] = {};

        // the default is the full character set, so if it's been loaded, we're done.
        if (this.cache[font_file_id]['default'])
            return this.cache[font_file_id]['default'];

        // if we're only loading the pangram, and the pangram has been loaded, we're done.
        if (specimenTextOnly && this.cache[font_file_id]['pangram']) {
            return this.cache[font_file_id]['pangram'];
        }

        const subset = specimenTextOnly ? 'pangram' : 'default';
        const filename = `${font_file_id}${
            subset != 'default' ? '-' + subset : ''
        }`;
        const path = `${this.fontServerAddress}/catalog/fonts/${font_file_id}/${filename}`;
        const source = `url("${path}.woff2") format("woff2"), url("${path}.woff") format("woff")`;
        const display = 'block';
        const specimen_text =
            font.specimen_text.toLocaleLowerCase() +
            font.specimen_text.toUpperCase();
        const unicodeRange = specimenTextOnly
            ? stringToUnicodeRange(specimen_text)
            : undefined;

        const descriptors: FontFaceDescriptors = {
            unicodeRange,
            display,
        };

        const fontface = new FontFace(target, source, descriptors);
        // clear cache if loading failed.
        fontface.loaded.catch((error) => {
            console.error('font face loading failed', error);
            delete this.cache[font_file_id][subset];
        });
        this.cache[font_file_id][subset] = fontface;
        document.fonts.add(fontface);
        fontface.load();

        return this.cache[font_file_id][subset];
    }
}
